import { useCallback, useMemo } from 'react'

import { FiltersDefinition, FiltersState } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

export interface LegalEntityForCurrencyFilter {
  id: string
  currency: string | null
  legalName: string | null
}

interface UseCurrencyFilteredEmployeeSelectorParams {
  currency: string
  legalEntities: LegalEntityForCurrencyFilter[]
}

interface FilterOption {
  label: string
  value: string
}

export interface StaticInFilterDefinition {
  label: string
  type: 'in'
  options: {
    cache: boolean
    options: () => FilterOption[]
  }
}

interface MapExtraFiltersParams {
  filters?: FiltersState<FiltersDefinition> | undefined
}

interface UseCurrencyFilteredEmployeeSelectorReturn {
  currencyMatchingLegalEntityIds: string[]
  currencyFilteredLegalEntityFilter: StaticInFilterDefinition
  mapExtraFiltersToVariables: (params: MapExtraFiltersParams) => { legalEntityIds: string[] }
}

export const useCurrencyFilteredEmployeeSelector = ({
  currency,
  legalEntities,
}: UseCurrencyFilteredEmployeeSelectorParams): UseCurrencyFilteredEmployeeSelectorReturn => {
  const currencyMatchingLegalEntityIds = useMemo(
    () => legalEntities.filter((le) => le.currency === currency).map((le) => le.id),
    [legalEntities, currency]
  )

  const currencyFilteredLegalEntityFilter: StaticInFilterDefinition = useMemo(
    () => ({
      label: i18n.t('employees.employees_list.filters.legal_entity'),
      type: 'in' as const,
      options: {
        cache: false,
        options: () =>
          legalEntities
            .filter((le) => le.currency === currency)
            .map((le) => ({
              label: le.legalName ?? `Legal Entity ${le.id}`,
              value: le.id,
            })),
      },
    }),
    [legalEntities, currency]
  )

  const mapExtraFiltersToVariables = useCallback(
    ({ filters }: MapExtraFiltersParams) => {
      const rawLegalEntities = filters?.legalEntity
      const userSelectedLegalEntities =
        Array.isArray(rawLegalEntities) &&
        rawLegalEntities.every((id): id is string => typeof id === 'string')
          ? rawLegalEntities
          : undefined
      return {
        legalEntityIds: userSelectedLegalEntities?.length
          ? userSelectedLegalEntities
          : currencyMatchingLegalEntityIds,
      }
    },
    [currencyMatchingLegalEntityIds]
  )

  return {
    currencyMatchingLegalEntityIds,
    currencyFilteredLegalEntityFilter,
    mapExtraFiltersToVariables,
  }
}
